import api from '../api';

/**
 * Form Service
 * Handles all form-related API calls
 */

const formService = {
  /**
   * Get all forms for the authenticated user
   * @param {string} status - Optional filter by status ('draft', 'published', 'archived')
   * @returns {Promise<Object>} List of forms
   */
  getForms: async (status = null) => {
    try {
      const params = status ? { status } : {};
      const response = await api.get('/forms', { params });
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get a single form by ID
   * @param {string} formId - Form ID
   * @returns {Promise<Object>} Form data
   */
  getForm: async (formId) => {
    try {
      const response = await api.get(`/forms/${formId}`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create a new form
   * @param {Object} formData - { title, description, questions, settings }
   * @returns {Promise<Object>} Created form
   */
  createForm: async (formData) => {
    try {
      const response = await api.post('/forms', formData);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update an existing form
   * @param {string} formId - Form ID
   * @param {Object} formData - { title, description, questions, settings }
   * @returns {Promise<Object>} Updated form
   */
  updateForm: async (formId, formData) => {
    try {
      const response = await api.put(`/forms/${formId}`, formData);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a form
   * @param {string} formId - Form ID
   * @returns {Promise<Object>} Success message
   */
  deleteForm: async (formId) => {
    try {
      const response = await api.delete(`/forms/${formId}`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Publish a form
   * @param {string} formId - Form ID
   * @returns {Promise<Object>} Published form
   */
  publishForm: async (formId) => {
    try {
      const response = await api.post(`/forms/${formId}/publish`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Unpublish a form (set back to draft)
   * @param {string} formId - Form ID
   * @returns {Promise<Object>} Updated form
   */
  unpublishForm: async (formId) => {
    try {
      const response = await api.post(`/forms/${formId}/unpublish`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Duplicate a form
   * @param {string} formId - Form ID
   * @returns {Promise<Object>} Duplicated form
   */
  duplicateForm: async (formId) => {
    try {
      const response = await api.post(`/forms/${formId}/duplicate`);
      return response;
    } catch (error) {
      throw error;
    }
  }
};

export default formService;
